import { BaseView } from "./base-view";
import { Navigation } from "../navigation";
import { showResourceDetailModal } from "./resource-detail-view";
import { PageContext } from "../page-context";
import { AssignmentDemand } from "../models/AssignmentDemand";
import { Skill } from "../models/Skill";
import { closeModal, createEmployeePhotoElement, createNameIconElementWith2Initials, fireBaseLog, getWeekNumber, percentageToColorWithInvert, replaceAccentedCharacters, showModal } from "../common";
import { CellComponent, ColumnDefinition, Filter, TabulatorFull } from "tabulator-tables";
import "select2";
import "select2/dist/css/select2.min.css";
import { Employee } from "../models/Employee";

export class ResourceFinderView extends BaseView {
    static title = "Resource Finder";
    static icon = "bi bi-person-check";
    unit: string;
    employeeAssignments!: AssignmentDemand[];
    skillStructure!: { [key: string]: string[] };
    projectRoles!: string[];
    assignment: AssignmentDemand | null = null;
    seniorities!: string[];
    locations!: string[];
    skillCategoryAndName: Skill[] = [];
    skillNameAndCategory: { [key: string]: string };
    filterOptions: { skill: { name: string; list: string; }; };
    /**
     * constructor
     * @param {PageContext} context - The context of the page.
     * @param {string} [container=".main"] - The container to render the view in.
     */
    constructor(context: PageContext, container = ".main") {
        super(context, container);

        this.unit = "hrs";

        let versionCheck = this.checkVersionAsync();

        Promise.all([versionCheck]).then(() => {
            let dataLoad = [
                context.store.getEmployeeAssignments(),
                context.store.getSkillStructure(),
                context.store.getProjectRoles(),
                context.store.getAssignment(this.context.params.assignmentId),
                context.store.getSeniorities(),
                context.store.getLocations(),
            ]

            Promise.all(dataLoad).then((values) => {
                this.employeeAssignments = values[0] as AssignmentDemand[];
                this.skillStructure = values[1] as { [key: string]: string[] };
                this.projectRoles = values[2] as string[];
                this.assignment = values[3] as AssignmentDemand | null;
                this.seniorities = values[4] as string[];
                this.locations = values[5] as string[];

                Object.keys(this.skillStructure).forEach((category) => {
                    this.skillStructure[category].forEach((skill) => {
                        this.skillCategoryAndName.push(new Skill(category, skill));
                        this.skillNameAndCategory[skill.toLowerCase()] = category;
                    });
                });

                this.onDataLoaded();
            });
        });

        this.unit = "hrs";
        this.filterOptions = {
            skill: { name: "Skill", list: "skillStructure" },
        };
        this.skillCategoryAndName = [];
        this.skillNameAndCategory = {};
    }

    connectedCallback(): this {
        super.connectedCallback();
        return this;
    }

    onDataLoaded() {

        this.innerHTML = `
        <div>
            <h1><i class="${ResourceFinderView.icon}"></i> Resource Finder</h1>
            <p>
                Find resources for your project.
                <a class="help-page-link-icon" href="#/help#resource-finder" title="Help page">
                    <i class="bi bi-info-circle"></i>
                </a>
            </p>
            <div class="alert alert-info demand-context-info" style="display:none" role="alert"></div>

            <div class="row mb-1">
                <div class="col-auto me-auto">
                    <button accesskey="r" class="btn btn-sm btn-outline-secondary btn-refresh" title="Reload data"><i class="bi bi-arrow-clockwise"></i></button>
                    <button class="btn btn-sm btn-outline-secondary btn-clearfilters" title="Clear all filters" style="display: none"><i class="bi bi-x-circle"></i></button>
                </div>

                <div class="col-auto">
                <button accesskey="e" class="btn btn-sm btn-outline-secondary btn-export-csv" title="Export CSV"><i class="bi bi-cloud-arrow-down"></i></button>
                <button accesskey="c" class="btn btn-sm btn-outline-secondary btn-toggle-cols" title="Show columns"><i class="bi bi-layout-three-columns"></i></button>
                    <div id="unit" class="visually-hidden btn-group btn-group-sm btns-units px-2" role="group" aria-label="Basic example">
                        <button type="button" class="btn btn-secondary" title="Hours">hrs</button>
                        <button type="button" class="btn btn-outline-secondary" title="Project days">pd</button>
                        <button type="button" class="btn btn-outline-secondary" title="Percentage">%</button>
                    </div>
                </div>
            </div>
        </div>
        
        <div id="resource-table" class="remaining-height"></div>
        `;

        const elem = $(this);

        elem.find(".btn-export-csv").click((e) => {
            this.table.download("csv", "export.csv");
        });

        elem.find(".btn-refresh").click((e) => {
            this.showLoading();
            this.context.store.clearStoreLocalStorage("employees");
            this.context.store.getEmployeeAssignments().then((data) => {
                this.employeeAssignments = data;
                const currentCalendarWeek = getWeekNumber() + "_" + new Date().getFullYear();
                const cwField = "cw_" + currentCalendarWeek;
                this.employeeAssignments.forEach((employeeAssignment) => {
                    if (employeeAssignment.employee)
                        employeeAssignment.employee.score = 1;
                    employeeAssignment.cw_planned = employeeAssignment.plannedWeeks[cwField];
                });
                this.table.replaceData(this.employeeAssignments);
                this.table.replaceData(data);
                this.hideLoading();
            });
        });

        elem.find(".btns-units").click((e) => {
            this.unit = $(e.target).text();
            elem.find(".btns-units button").removeClass("btn-secondary").addClass("btn-outline-secondary");
            $(e.target).removeClass("btn-outline-secondary").addClass("btn-secondary");
        });

        elem.find(".btn-clearfilters").click((e) => {
            this.table.clearFilter(true);
            $(".btn-clearfilters").hide();
        })

        if (this.assignment) {
            elem.find(".demand-context-info").show()
                .html(`Showing resources for demand assignment <a class="demand-context-info-close-window cursor-pointer">${this.assignment.description}</a>`);
        }
        //previous interaction: return to demand view using href='#/demands/detail?assignmentId=${this.assignment.id}'

        elem.find(".demand-context-info-close-window").click((e) => {
            window.close();
        })
        this.context.store.assignmentChangeUnsubscribeAll();
        this.addTable();
        this.columnToggler($(".btn-toggle-cols")[0], this.table);
    }

    addTable() {
        let skillFilterDirty = false;
        const columns: ColumnDefinition[] = [
            {
                title: "Score",
                field: "employee.score",
                visible: false,
                width: 50,
                vertAlign: "middle"
            },
            {
                title: "Employee",
                field: "employee.employeeName",
                frozen: true,
                headerFilter: "input",
                headerFilterFunc: function (headerValue, rowValue) {
                    if (!rowValue || !headerValue) return false;
                    return replaceAccentedCharacters(rowValue.toLowerCase()).includes(replaceAccentedCharacters(headerValue.toLowerCase()));
                },
                width: 250,
                formatter: this.namesWithIconFormatter,
                tooltip: true
            },
            {
                title: "Seniority",
                field: "employee.seniority",
                headerSort: true,
                width: 100,
                headerFilter: this.createMultiselectHeaderFilter(this.seniorities),
                headerFilterFunc: "in",
                formatter: "plaintext",
                tooltip: true,
                vertAlign: "middle"
            },
            {
                title: "Role",
                field: "employee.role",
                headerSort: true,
                width: 150,
                headerFilter: this.createMultiselectHeaderFilter(this.projectRoles),
                headerFilterFunc: "in",
                formatter: "plaintext",
                tooltip: true,
                vertAlign: "middle"
            },
            {
                title: "Location",
                field: "employee.location",
                headerSort: true,
                width: 100,
                headerFilter: this.createMultiselectHeaderFilter(this.locations.sort()),
                headerFilterFunc: "in",
                formatter: "plaintext",
                tooltip: true,
                vertAlign: "middle",
                visible: false,
            },
            {
                title: "Skills",
                field: "employee.skills",
                headerSort: false,
                headerFilter: this.createMultiselectHeaderFilter(this.skillCategoryAndName.map(skill => skill.name)),
                headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
                    skillFilterDirty = true;
                    return true;
                },
                formatter: this.SkillsFormatter,
                tooltip: function (e, cell) {
                    return cell.getElement().textContent || "";
                },
                vertAlign: "middle"
            },
            {
                title: "Planned in CW",
                field: "cw_planned",
                headerFilter: "number",
                headerFilterPlaceholder: "at max...",
                headerFilterFunc: "<=",
                formatter: this.cwCellFormatter,
                formatterParams: { maxHours: 40 },
                width: 180,
                vertAlign: "middle"
            },
        ];

        const currentCalendarWeek = getWeekNumber() + "_" + new Date().getFullYear();
        const cwField = "cw_" + currentCalendarWeek;
        this.employeeAssignments.forEach((employeeAssignment) => {
            if (employeeAssignment.employee)
                employeeAssignment.employee.score = 1;
            employeeAssignment.cw_planned = employeeAssignment.plannedWeeks[cwField];
        });

        this.columnToggleFromLocalStorage(columns);
        const table = new TabulatorFull("#resource-table", {
            height: 200,
            layout: "fitColumns",
            data: this.employeeAssignments,
            columns: columns,
            initialSort: [
                { column: "employee.employeeName", dir: "asc" },
            ],
            headerSortClickElement: "icon"
            // initialFilter:[
            //     {field:"score", type:"!=", value:"0"}
            // ],
        });
        table.on("tableBuilt", this.onTableBuilt.bind(this));
        table.on("rowClick", (e, row) => {
            row.getTable().deselectRow();
            row.select();
            const employeeAssignment = row.getData();
            const employee = employeeAssignment.employee;
            Navigation.navigateHash("/resource-finder/detail", { ...this.context.params, employeeId: employee.id }, true);
            showResourceDetailModal(employee, this.context);
        });

        table.on("dataFiltered", (filters) => {
            //enable clear all filters button
            $(".btn-clearfilters").show();
            //filters - array of filters currently applied
            const skillFilter = filters.find((filter) => filter.field === "employee.skills");
            if (skillFilter && Array.isArray(skillFilter.value) && skillFilterDirty) {
                skillFilterDirty = false; // reset dirty flag
                const skillFilters = skillFilter.value.map((skill) => { return { value: skill } });

                for (const row of table.getRows()) {
                    const employeeAssignment = row.getData() as AssignmentDemand;
                    const employee = employeeAssignment.employee;
                    if (employee)
                        employee.score = this._score(employee, skillFilters);
                    row.update(employeeAssignment);
                }
                if (table.rowManager.displayRowsCount > 0) {
                    this.table.setSort([{ column: "employee.score", dir: "desc" }]);
                    this.table.redraw(true);
                }
                fireBaseLog('resource_skill_search', {
                    filters: skillFilters.map(filter => ({ value: filter.value })),
                });
            }
            if (this.table.getRows().length > 0 && filters.length > 0) {
                this.storeFilter(filters);
            }
        });
        this.table = table;
        return this;
    }

    createMultiselectHeaderFilter(items: string[]) {
        return (cell: CellComponent, onRendered: (arg0: () => void) => void, success: (arg0: string | string[] | undefined) => void) => {
            var input = document.createElement("select");
            input.style.width = "100%";
            input.style.height = "auto";
            input.setAttribute("multiple", "");

            items.forEach((item) => {
                input.add(new Option(item, item));
            });

            onRendered(() => {
                const $input = $(input);
                $input.select2({
                    closeOnSelect: false,
                    width: "100%",
                }).on("change", () => {
                    success($input.val());
                    this.table.redraw(true);
                })
                    .on("select2:select", () => {
                        const $searchField = $input.data('select2').dropdown.$search || $input.data('select2').selection.$search;
                        if ($searchField) {
                            $searchField.val('').trigger('input');
                            $searchField.focus();
                        }
                    }).on('select2:unselect', () => {
                        const $searchField = $input.data('select2').dropdown.$search || $input.data('select2').selection.$search;
                        if ($searchField) {
                            $searchField.val('').trigger('input');
                            $searchField.focus();
                        }
                    });
            });

            return input;
        };
    }

    async onTableBuilt() {
        if (this.context.params.filters) {
            for (const filter of this.context.params.filters) {
                this.table.setHeaderFilterValue(filter.field, filter.value);
            }
            //apply filters from hash
        }

        this.table.redraw(true);

        // expand the row if we have an employeeId in the context
        if (this.context.params.employeeId) {
            let found = false;
            for (const row of this.table.getRows()) {
                const employeeAssignment = row.getData();
                if (employeeAssignment.employee.id === this.context.params.employeeId) {
                    found = true;
                    var employee = employeeAssignment.employee;
                    //TODO check if this was working?
                    //this.table.scrollToRow(row);
                    row.select();
                    //this.table.selectRow(employee.id);
                    Navigation.restorePreviousHashUrl();
                    showResourceDetailModal(employee, this.context);
                }
            }
            if (!found) {
                showModal('<i class="bi bi-exclamation-triangle"></i> Resource not found', "The resource <b>" + this.context.params.employeeId + "</b> is unknown.", this.context)
            }
        } else {
            closeModal();
        }
    }

    storeFilter(filters: Filter[]) {
        // add parameter to hash so that it can be bookmarked
        const params = { ...this.context.params, filters: filters };
        Navigation.navigateHash(null, params, true);
    }

    _score(employee: Employee, skillFilters: { value: any; }[]) {
        var skills = employee.skills;
        var score = 0;

        for (const filter of skillFilters) {
            var filterSkill = filter.value.toLowerCase();
            // filterSkill = filterSkill.split(" - ")[1];
            //score skills exact match
            var skill = skills.all().find(function (skill) {
                return skill.name.toLowerCase() === filterSkill;
            });
            if (skill) {
                score += 1 + skill.level;
            }

            //score skills in the same category
            var filterSkillCategory = this.skillNameAndCategory[filterSkill];
            score += skills.all().filter(function (skill) {
                return skill.category === filterSkillCategory;
            }).length * 0.15;
        };
        return Math.round(score * 100) / 100;
    }

    /**
     * @param {Tabulator.CellComponent} cell
     * @param {object} formatterParams
     * @returns
     */
    cwCellFormatter(cell: CellComponent) {
        var bvid = cell.getValue();
        //smooth color fade cells based on value
        if (bvid !== 0) {
            cell.getElement().style.backgroundColor = percentageToColorWithInvert((40 - bvid) / 40);
        }

        if (this.unit == "pd") {
            return bvid / 8;
        } else if (this.unit == "%") {
            return Math.round(bvid / 40 * 100) + "%"; //todo: align with max hours
        }
        return bvid;
    }

    SkillsFormatter(cell: CellComponent) {
        let table = cell.getTable();
        let skills = cell.getValue();
        let filterValue = table.getColumn("employee.skills").getHeaderFilterValue();
        let filteredCategories: string[] = filterValue ? [...new Set((filterValue as string[]).map((filter: string) => filter.split(" - ")[0]))] : [] as string[];
        let str = "";

        let skillsMap: { [key: string]: Skill[] } = {};
        for (let skill of skills.skills) {
            if (!skillsMap[skill.category]) {
                skillsMap[skill.category] = [];
            }
            skillsMap[skill.category] = skillsMap[skill.category].concat(skill);
        }

        for (let category of filteredCategories) {
            if (skillsMap[category]) {
                str += `${category}: ${skillsMap[category].join(", ")} | `;
            }
        }

        for (let category in skillsMap) {
            if (!filteredCategories.includes(category) || !filterValue) {
                str += `${category}: ${skillsMap[category].join(", ")} | `;
            }
        }

        // Remove the last " | " from the string
        if (str.endsWith(" | "))
            str = str.slice(0, -3);

        return str;
    }

    /**
     * Create circles with color based on value and fist letters of names
     * @param {Tabulator.CellComponent} cell
     * @param {object} formatterParams
     * @returns {string}
    */
    namesWithIconFormatter(cell: CellComponent) {
        const bvid = cell.getValue();
        let row = cell.getRow();
        let data = row.getData();

        const iconElement = data.employee && data.employee.photo
            ? createEmployeePhotoElement(data.employee.photo, "name-cell-icon mx-2")
            : createNameIconElementWith2Initials(bvid, "name-cell-icon mx-2 p-1");

        return iconElement + `<span>${bvid}</span>`;
    }
}
